import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '@/store';
import { X, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/announcements')
      .then((r) => {
        const list = r.data.announcements || [];
        if (list.length) { setItems(list); setTimeout(() => setOpen(true), 400); }
      })
      .catch(() => {});
  }, []);

  const current = items[idx];

  const dismissCurrent = (goNav) => {
    const a = current;
    if (!a) return;
    api.post(`/announcements/${a.id}/dismiss`).catch(() => {});
    const rest = items.filter((x) => x.id !== a.id);
    if (goNav && a.link) navigate(a.link);
    if (rest.length) {
      setItems(rest);
      setIdx((i) => Math.min(i, rest.length - 1));
    } else {
      setOpen(false);
      setTimeout(() => setItems([]), 300);
    }
  };

  const dismissAll = () => {
    items.forEach((a) => api.post(`/announcements/${a.id}/dismiss`).catch(() => {}));
    setOpen(false);
    setTimeout(() => setItems([]), 300);
  };

  if (!items.length || !current) return null;

  return createPortal(
    (<div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      onClick={dismissAll}
    >
      {/* fundo */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

      {/* card */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`ann-pop relative w-full max-w-md rounded-3xl overflow-hidden border border-primary/30 bg-[#0c1420] shadow-2xl transition-all duration-300 ${open ? 'scale-100' : 'scale-90'}`}
      >
        {/* aura de brilho animada no topo */}
        <div className="ann-aura" />
        <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-primary/25 blur-3xl" />

        <button onClick={dismissAll}
          className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-muted-foreground hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>

        <div className="relative px-7 pt-10 pb-7 text-center">
          {/* ícone grande com pulso */}
          <div className="mx-auto mb-5 w-20 h-20 rounded-3xl bg-gradient-to-br from-primary/25 to-primary/5 border border-primary/25 flex items-center justify-center text-4xl ann-float">
            {current.icon || <Sparkles className="w-9 h-9 text-primary" />}
          </div>

          <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-primary bg-primary/15 px-3 py-1 rounded-full mb-3">
            ✨ Novidade
          </span>
          <h2 className="text-xl font-bold text-white">{current.title}</h2>
          <p className="text-sm text-muted-foreground mt-2.5 leading-relaxed">{current.text}</p>

          {/* ações */}
          <div className="mt-6 flex flex-col gap-2">
            {current.link && current.cta && (
              <button onClick={() => dismissCurrent(true)}
                className="ann-cta w-full font-bold text-primary-foreground rounded-xl py-3 text-sm">
                {current.cta}
              </button>
            )}
            <button onClick={() => dismissCurrent(false)}
              className="w-full text-sm font-medium text-muted-foreground hover:text-white py-2.5 transition-colors">
              {items.length > 1 ? 'Ver próximo' : 'OK, entendi'}
            </button>
          </div>
        </div>

        {/* paginação (bolinhas) quando há vários */}
        {items.length > 1 && (
          <div className="relative flex items-center justify-center gap-3 pb-5">
            <button onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
              className="text-muted-foreground hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex gap-1.5">
              {items.map((_, i) => (
                <span key={i} onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full cursor-pointer transition-all ${i === idx ? 'w-5 bg-primary' : 'w-1.5 bg-white/20'}`} />
              ))}
            </div>
            <button onClick={() => setIdx((i) => (i + 1) % items.length)}
              className="text-muted-foreground hover:text-white"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes ann-pop-in { 0% { transform: scale(.9) translateY(20px); opacity: 0; } 100% { transform: scale(1) translateY(0); opacity: 1; } }
        .ann-pop { animation: ann-pop-in .4s cubic-bezier(.2,1.3,.4,1) both; }
        @keyframes ann-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .ann-float { animation: ann-float 3s ease-in-out infinite; }
        .ann-aura { position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(90deg,transparent,hsl(var(--primary)),transparent); background-size:200% 100%; animation: ann-slide 2.5s linear infinite; }
        @keyframes ann-slide { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        .ann-cta { background:linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary)/.8)); box-shadow:0 8px 24px hsl(var(--primary)/.35); transition: transform .15s, box-shadow .15s; }
        .ann-cta:hover { transform: translateY(-2px); box-shadow:0 12px 32px hsl(var(--primary)/.5); }
        .ann-cta:active { transform: scale(.97); }
      `}</style>
    </div>),
    document.body
  );
}
